import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from 'store/User/Context';
import { saleOffersApiRequests } from 'utils/api-requests/sale';
import { isTouchDevice } from 'utils/checkIsTouchDevice';
import SaleContext from 'utils/context/SaleContext';
import { encodeData } from 'utils/encodeAndDecodeFunctions';
import { formatFilterPrice } from 'utils/formatFilterPrice';
import { formatPriceBeforeModification } from 'utils/formatters/formatPriceBeforeModification';
import { getSubModuleByType } from 'utils/formatters/getSubModuleByType';
import { normalizeFlipResult } from 'utils/formatters/normalizeFlipResult';
import { getStringFromDate } from 'utils/getStringFromDate';
import { getFirstPartLink } from 'utils/helpers/firstPartCopyLinkHelper';
import { getSaleFiltersFromState, getSaleValueFromState } from 'utils/state-managment/sale/saleFilter';
import { SaleOfferDuplicatesResponse, SaleOfferResponseContent } from 'utils/state-managment/sale/saleOffer';
import { AllModuleTypes } from 'utils/types/AllModules';
import { CalculatorSettings } from 'utils/types/Calculator';
import { MultipleFilterType, ObjectAndSingleValueChangeType } from 'utils/types/InputTypes';
import { UserReactions } from 'utils/types/OfferData';
import { ReactionType } from 'utils/types/ReactionType';
import { SaleFiltersStateType } from 'utils/types/Sale';
import { Module } from 'utils/types/SaleModels';
import { hasRole, UserRole } from 'utils/userRoleHelper';

import OfferActions from 'components/common/Card/common/OfferActions/OfferActions';
import { InteractiveElements } from 'components/common/Card/common/V2InteractiveIconsWrapper/V2InteractiveIconsWrapper';
import { CardData } from 'components/common/Card/V2BaseCard';
import { IconEnum } from 'components/common/Icon';
import { SliderImages } from 'components/common/Slider/Slider';
import { RaisesTabContentProps } from 'components/common/Tabs/tabs/RaisesTab';
import { V2DuplicatesTabProps } from 'components/common/Tabs/tabs/V2DuplicatesTab';
import { TabType } from 'components/common/Tabs/V2Tabs';
import { convertToThumbnailPath, createImagesForSlider } from 'components/functions/imagesFunctions';
import {
    saleOfferListFunctions
} from 'components/functions/offerListFunctions/saleOfferListFunctions/saleOfferListFunctions';
import { generateOfferActionsElements } from 'components/functions/shared/generateOfferActionsElements';
import V2OfferList, { RenderList } from 'components/offerList/V2OfferList';

const SaleOffers = () => {
    const {
        saleFiltersState,
        handleChange,
        saleOfferState,
        loadDuplicates,
        loadSaleOffers,
        deleteNote,
        updateNote,
        reactionUpdate,
        loadOfferDetailsAsync,
        loadNotesAsync,
        updateOpinion,
        deleteOpinion,
        loadOpinionsAsync,
        handleReservation,
        removeReservation,
        handleNotificationBlockedOfferToggle,
        calculatorState,
        infoElementsState,
        detailsInfoElementsState,
        setDetailsInfoElementClicked,
        setDetailsInfoElementHover,
        setInfoElementHover,
        setInfoElementClicked,
        activeInfoElementId,
        activeDetailInfoElementId,
        updateFlipCalculations,
        updateFlipCalculationTotalValue,
        handleCalculatorChange,
        resetFlipCalculationField,
        resetFlipCalculations,
        loadFlipCalculations
    } = useContext(SaleContext);
    const { user } = useUser();
    const [moreButtonActiveId, setMoreButtonActiveId] = useState<string | null>(null);
    const [organizationActiveId, setOrganizationActiveId] = useState<string | null>(null);
    const [renderList, setRenderList] = useState<RenderList[]>([]);
    const navigate = useNavigate();
    const hasPredictionRole = hasRole(UserRole.PREDICT_PRICE, user.userRole);
    const hasCalculationRole = hasRole(UserRole.CALCULATOR, user.userRole);

    const handleChangePage = useCallback((currentPage: number,
        rows = 25) => {
        loadSaleOffers({
            requestData: getSaleValueFromState(saleFiltersState),
            page: currentPage,
            rows
        });
    }, [saleFiltersState]);

    const handleActiveFiltersChange = () => {
        loadSaleOffers({ page: 1, rows: 25 });
    };

    const handleOlderAdverts = () => {
        const date = saleFiltersState.offerAdded.from ? new Date(new Date(saleFiltersState.offerAdded.from).setDate(new Date(saleFiltersState.offerAdded.from).getDate() - 6)) : null;
        const dateString = date && getStringFromDate(date.toDateString());

        loadSaleOffers({
            requestData: {
                ...getSaleValueFromState(saleFiltersState),
                ...{ offerAddedFrom: !saleFiltersState.daysAmount ? dateString ?? undefined : undefined },
                ...{ daysAmount: saleFiltersState.daysAmount ? saleFiltersState.daysAmount + 6 : undefined }
            },
            page: saleOfferState.saleOfferList?.pageable?.pageNumber,
            rows: saleOfferState.saleOfferList && saleOfferState.saleOfferList.size ? saleOfferState.saleOfferList.size : 25
        });
    };

    const handleSaveOrUpdateOpinion = async (offer: SaleOfferResponseContent,
        rating: number,
        description: string) => {
        const opinionId = offer.myOpinion?.id || undefined;
        updateOpinion(offer.module, description, rating, offer.advertisementId, opinionId);
    };

    const onNoteAdd = () => {
        // TODO: INVEST-257 Czy ta funkcja jest potrzebna?
        // if (!showNote && showLastNote) {
        //     showLastNote();
        // }
        //
        // if (!data.note?.noteId) {
        //     openOfferTab('note');
        // }
    };

    const onMessageClick = () => {
        onNoteAdd();
    };

    const handleDelete = (noteId: number,
        id: number,
        type: Module) => {
        deleteNote(id, type, noteId);
    };

    const handleSaveNote = async (value: string,
        advertId: number,
        type: Module,
        id?: number) => {
        updateNote(type, value, advertId, id || 0);
    };

    const toggleContainerDetails = (detailsOpen: boolean,
        offer: SaleOfferResponseContent) => {
        if (!detailsOpen) {
            offer.reaction && !offer.reaction.viewedStatus
                ? reactionUpdate(offer.advertisementId, offer.location!, {
                    ...offer.reaction,
                    openedStatus: true,
                    viewedStatus: true
                } as ReactionType, offer.module)
                : null;
            loadOfferDetailsAsync(offer.advertisementId, offer.module);
        }
    };

    const loadDetails = (id: number,
        module: Module) => {
        loadOfferDetailsAsync(id, module);
    };

    const handleChangeReaction = (name: keyof ReactionType,
        value: boolean,
        offer: SaleOfferResponseContent) => {
        const userReaction = Object.assign({}, offer.reaction);

        if (name === 'likeStatus' && userReaction.unlikeStatus && value) {
            userReaction.unlikeStatus = false;
        } else if (name === 'unlikeStatus' && userReaction.likeStatus && value) {
            userReaction.likeStatus = false;
        }

        (userReaction[name] as boolean) = value;
        const city = offer.location;

        reactionUpdate(offer.advertisementId, city, userReaction as UserReactions, offer.module);
    };

    const handleDeleteFilterValue = (filterName: string,
        value?: string | number | null | string[],
        index?: number) => {
        const currentValue = saleFiltersState[filterName as keyof SaleFiltersStateType];

        if (index !== undefined && Array.isArray(currentValue)) {
            const newArray = [...currentValue as (string | number | null)[]];
            newArray.splice(index, 1);
            handleChange(filterName, newArray as ObjectAndSingleValueChangeType);
        } else if (
            typeof currentValue === 'object' &&
            currentValue !== null &&
            'from' in (currentValue as object) &&
            'to' in (currentValue as object)
        ) {
            const rangeFilter = currentValue as MultipleFilterType;
            let updatedFrom = rangeFilter.from;
            let updatedTo = rangeFilter.to;

            if (value === rangeFilter.from) {
                updatedFrom = '';
            } else if (value === rangeFilter.to) {
                updatedTo = '';
            }

            handleChange(filterName, { from: updatedFrom, to: updatedTo } as ObjectAndSingleValueChangeType);
        } else {
            handleChange(filterName, null as unknown as ObjectAndSingleValueChangeType);
        }
    };

    const firstPartLink = getFirstPartLink();

    const generateInteractiveElements = (offer: SaleOfferResponseContent): InteractiveElements[] => {
        const offerLink = `${firstPartLink}/sale/${getSubModuleByType(offer.module)}/${offer.encryptedId}`;
        const submodule = getSubModuleByType(offer.module);
        const isMoreButtonActive = moreButtonActiveId === offer.advertisementId.toString();
        const similarOfferAction = {
            children: <><p>Pokaż podobne ogłoszenia</p></>,
            onClick: async (e: MouseEvent) => {
                e.stopPropagation();
                const filterData = await saleOffersApiRequests.getSimilarOffersFilter(offer.advertisementId, offer.module);

                if (filterData) {
                    const encoded = encodeData({
                        ...filterData,
                        locations: filterData.locations || [],
                        _timestamp: Date.now()
                    });
                    window.open(`${window.location.origin}/ai-filter/${encoded}`, '_blank');
                }
            }
        };
        const offerActionsElements = generateOfferActionsElements(offerLink, offer.link, [similarOfferAction], offer.encryptedId !== null);
        const originalEncryptedId = offer.originalEncryptedId !== null && offer.originalEncryptedId !== undefined;
        const originalOfferLink = `${window.location.origin}/sale/${submodule}/${offer.originalEncryptedId}`;

        user.isInOrganization
            ? offerActionsElements.push({
                children:
                    <>
                        <p>Prezentuj jako pośrednik</p>
                    </>,
                onClick: async (e: MouseEvent) => {
                    e.stopPropagation();

                    navigate(`/sale/${submodule}/${offer.advertisementId}/copy`);
                }
            })
            : null;

        if (originalEncryptedId) {
            offerActionsElements.push({
                children:
                    <>
                        <p>Przejdź do oryginalnego ogłoszenia</p>
                    </>,
                onClick: async (e: MouseEvent) => {
                    e.stopPropagation();

                    window.open(originalOfferLink, '_blank');
                }
            });
        }

        return [
            {
                tooltipText: 'Wyświetlono',
                icon: IconEnum.EYE,
                active: offer.reaction?.viewedStatus,
                disabled: offer.reaction?.viewedStatus === undefined,
                onClick: () => handleChangeReaction('viewedStatus', !offer.reaction?.viewedStatus, offer)
            },
            {
                tooltipText: 'Lubię to',
                icon: IconEnum.LIKE,
                active: offer.reaction?.likeStatus,
                onClick: () => handleChangeReaction('likeStatus', !offer.reaction?.likeStatus, offer)
            },
            {
                tooltipText: 'Nie lubię tego',
                icon: IconEnum.DISLIKE,
                active: offer.reaction?.unlikeStatus,
                disabled: offer.reaction?.unlikeStatus === undefined,
                onClick: () => handleChangeReaction('unlikeStatus', !offer.reaction?.unlikeStatus, offer)
            },
            {
                tooltipText: 'Notatki',
                icon: IconEnum.MESSAGE,
                active: offer.numberOfNotes ? offer.numberOfNotes > 0 : false,
                counter: offer.numberOfNotes ? offer.numberOfNotes || 1 : undefined,
                openTabOnClick: TabType.NOTES,
                onClick: onMessageClick
            },
            {
                tooltipText: 'Ogłoszenie nieaktualne',
                icon: IconEnum.BLOCKED,
                active: offer.reaction?.hideStatus,
                disabled: offer.reaction?.hideStatus === undefined,
                onClick: () => handleChangeReaction('hideStatus', !offer.reaction?.hideStatus, offer)
            },
            {
                tooltipText: 'Przeprowadzona rozmowa',
                icon: IconEnum.PHONE,
                active: offer.reaction?.phoneStatus,
                disabled: offer.reaction?.phoneStatus === undefined,
                onClick: () => handleChangeReaction('phoneStatus', !offer.reaction?.phoneStatus, offer)
            },
            {
                tooltipText: 'Umówione spotkanie',
                icon: IconEnum.PEOPLES_3,
                active: offer.reaction?.arrangedStatus,
                disabled: offer.reaction?.arrangedStatus === undefined,
                onClick: () => handleChangeReaction('arrangedStatus', !offer.reaction?.arrangedStatus, offer)
            },
            ...offer.phoneNumber
                ? [
                    {
                        icon: IconEnum.PHONE,
                        isLink: true,
                        active: false,
                        isPhoneBtn: false,
                        text: <a href={`tel:${offer.phoneNumber}`}>{offer.phoneNumber}</a>
                    }
                ]
                : [],
            ...offer.photos && offer.photos.length > 0
                ? [
                    {
                        icon: IconEnum.CAMERA,
                        isModalOpen: true,
                        isLink: true,
                        text: <p>Zobacz zdjęcia</p>,
                        isPhotoBtn: true,
                        visibleDuringModal: true,
                        sliderElements: {
                            photos: createImagesForSlider(offer.photos, offer.module) as SliderImages[],
                            title: offer.title
                        }
                    }
                ]
                : [],
            {
                icon: 'squares' as IconEnum,
                tooltipText: isTouchDevice() ? undefined : 'Więcej',
                onClick: () => isMoreButtonActive ? setMoreButtonActiveId(null) : setMoreButtonActiveId(offer.advertisementId.toString()),
                additionalJSXSibling: isMoreButtonActive
                    ? <OfferActions onClickOutside={() => setMoreButtonActiveId(null)}
                        elements={offerActionsElements}/>
                    : undefined
            }
        ];
    };

    const handleDuplicates = async (offer: SaleOfferResponseContent) => {
        loadDuplicates(offer.module, offer.advertisementId);
    };

    const handleNotes = async (offer: SaleOfferResponseContent) => {
        loadNotesAsync(offer.advertisementId.toString(), offer.module, true);
    };

    const generateOrganizationCardData = (offer: SaleOfferResponseContent) => {
        return {
            isReserved: offer.blockageInfo !== null,
            ...offer.blockageInfo
                ? {
                    reservationDate: offer.blockageInfo.blockedDate,
                    userName: offer.blockageInfo.userName,
                    isPermanentlyReserved: offer.blockageInfo.permanentlyBlocked,
                    myBlockage: offer.blockageInfo.myBlockage
                }

                : [],
            handleReservation: (days: number) => {
                handleReservation(days, offer.advertisementId, offer.module);
                setOrganizationActiveId(offer.advertisementId.toString());
            },
            removeReservation: () => {
                removeReservation(offer.advertisementId, offer.module);
                setOrganizationActiveId(null);
            },
            handleReservationPermanently: () => {
                handleReservation(0, offer.advertisementId, offer.module, true);
                setOrganizationActiveId(offer.advertisementId.toString());
            },
            handleToggleReservation: () => {
                handleNotificationBlockedOfferToggle(offer.advertisementId, offer.module, offer?.blockageInfo?.notificationStatus ?? false);
                setOrganizationActiveId(offer.advertisementId.toString());
            },
            notificationStatus: offer.blockageInfo?.notificationStatus,
            offerId: offer.advertisementId,
            offerModule: offer.module
        };
    };

    const generateCardData = (offer: SaleOfferResponseContent): CardData => {
        const subMainValues: React.ReactNode[] = [];

        if (offer.pricePerSquareMeter) {
            subMainValues.push(
                <><b>{offer.pricePerSquareMeter.toString()}</b> zł / m<sup>2</sup></>
            );
        }
        const infoElements = saleOfferListFunctions.generateInfoElements(offer, infoElementsState, hasPredictionRole, setInfoElementHover, setInfoElementClicked);

        const ratingContainer = {
            opinionsNumber: offer.numberOfCommunityOpinions,
            opinionsRating: offer.communityScore,
            isMyOpinion: !!offer.myOpinion,
            commentsCount: offer.numberOfCommunityComments,
            title: offer.title,
            userRatingValue: offer.myOpinion?.numberOfStars,
            userRatingComment: offer.myOpinion?.content || undefined,
            handleGetComments: () => handleNotes(offer),
            handleSendRating: (rating: number,
                description: string) => handleSaveOrUpdateOpinion(offer, rating, description)
        };

        return {
            preTitle: offer.predictedLocation ? `${offer.location}, ` : offer.location,
            additionalPreTitleString: offer.predictedLocation ? offer.predictedLocation : undefined,
            lowestPriceLabel: offer.duplicateWithLowestPrice,
            privateOfferLabel: offer.duplicateWithPrivateOffer,
            archivedLabel: offer.archived,
            oldPrice: formatPriceBeforeModification(offer.priceBeforeModification),
            date: offer.dateAdded,
            oldestDuplicateDateAdded: offer.oldestDuplicateDateAdded,
            dateIcon: saleOfferListFunctions.getDateIcon(offer.status),
            title: offer.title,
            withPrediction: true,
            infoElements: user.isInOrganization && offer.blockageInfo?.permanentlyBlocked
                ? undefined
                : infoElements,
            interactiveElements: generateInteractiveElements(offer),
            modifications: user.isInOrganization && offer.blockageInfo?.permanentlyBlocked
                ? undefined
                : {
                    duplicationsNumber: offer.numberOfDuplicates ?? 0,
                    modificationsNumber: offer.numberOfModifications ?? 0,
                    boostsNumber: offer.numberOfRaises ?? 0
                },
            note: offer.note,
            numberOfNotes: offer.numberOfNotes ? offer.numberOfNotes : undefined,
            getAllNotes: loadNotesAsync,
            offerId: offer.advertisementId,
            offerModule: offer.module as AllModuleTypes,
            adContext: { adId: offer.advertisementId, adModule: offer.module },
            modificationDate: offer.lastUpdated,
            thumbnailPath: offer.photos && offer.photos.length > 0 ? convertToThumbnailPath(offer.photos[0], offer.module) : './defaultImg.png',
            ratingScore: offer.score ?? 0,
            scorePrecision: offer.scorePrecision ?? 0,
            additionalInfoBadge: offer.offerFrom,
            predictedAdditionalInfoBadge: offer.predictedOfferFrom,
            images: createImagesForSlider(offer.photos || [], offer.module),
            mainValue: offer.price ? `${offer.price} zł` : '- zł',
            subMainValues,
            calculationValue: offer.flipResult && offer.flipResult.maxPurchasePrice ? offer.flipResult.maxPurchasePrice.toString() : undefined,
            hasCalculationRole,
            link: {
                url: offer.link,
                name: offer.portal
            },
            ...!user.isInOrganization
                ? { ratingContainer }
                : {
                    organizationAndRating: {
                        ratingContainer: user.isInOrganization && offer.blockageInfo?.permanentlyBlocked ? undefined : ratingContainer,
                        organizationContainer: generateOrganizationCardData(offer)
                    }
                },
            predictedPrice: offer.predictedPrice ? offer.predictedPrice.toString() : undefined,
            hasPredictionRole,
            isDuplicate: !saleFiltersState.groupDuplicate ? offer.originalEncryptedId !== null && offer.originalEncryptedId !== undefined : undefined,
            showLendiButtons: true,
            // @ts-expect-error AiOpinion has nullable content fields vs AiOpinionType requires string
            aiOpinion: offer.aiOpinion ? offer.aiOpinion : undefined
        };
    };

    const generateDuplicateCardData = (offer: SaleOfferDuplicatesResponse) => {
        const subMainValues: React.ReactNode[] = [];

        if (offer.pricePerSquareMeter) {
            subMainValues.push(
                <><b>{formatFilterPrice(offer.pricePerSquareMeter.toString())}</b> zł / m<sup>2</sup></>
            );
        }

        return {
            preTitle: offer.predictedLocation ? `${offer.location}, ` : offer.location,
            additionalPreTitleString: offer.predictedLocation ? offer.predictedLocation : undefined,
            oldPrice: formatPriceBeforeModification(offer.priceBeforeModification),
            date: offer.dateAdded,
            title: offer.title,
            infoElements: saleOfferListFunctions.generateInfoElements(offer as SaleOfferResponseContent, infoElementsState, hasPredictionRole, setInfoElementHover, setInfoElementClicked),
            interactiveElements: generateInteractiveElements(offer as SaleOfferResponseContent),
            modifications: {
                modificationsNumber: offer.numberOfModifications ?? 0,
                boostsNumber: offer.numberOfRaises ?? 0
            },
            note: offer.note,
            modificationDate: offer.lastUpdated,
            thumbnailPath: offer.photos ? offer.photos[0] : undefined,
            ratingScore: offer.score ?? 0,
            additionalInfoBadge: offer.offerFrom,
            images: createImagesForSlider(offer.photos || [], offer.module!),
            mainValue: offer.price ? `${formatFilterPrice(offer.price)} zł` : '- zł',
            subMainValues,
            calculationValue: offer.flipResult && offer.flipResult.maxPurchasePrice ? offer.flipResult.maxPurchasePrice.toString() : undefined,
            hasCalculationRole,
            predictedPrice: offer.predictedPrice ? offer.predictedPrice.toString() : undefined,
            hasPredictionRole,
            link: {
                url: offer.link,
                name: offer.portal
            }
        };
    };

    useEffect(() => {
        const offersList = saleOfferState.saleOfferList.content
            ? saleOfferState.saleOfferList?.content.map((offer): RenderList => {
                const isMoreButtonActive = moreButtonActiveId === `${offer.advertisementId}-detail`;
                const offerLink = `${firstPartLink}/sale/${getSubModuleByType(offer.module)}/${offer.encryptedId}`;
                const similarOfferAction = {
                    children: <><p>Pokaż podobne ogłoszenia</p></>,
                    onClick: async (e: MouseEvent) => {
                        e.stopPropagation();
                        const filterData = await saleOffersApiRequests.getSimilarOffersFilter(offer.advertisementId, offer.module);

                        if (filterData) {
                            const encoded = encodeData({
                                ...filterData,
                                locations: filterData.locations || [],
                                _timestamp: Date.now()
                            });
                            window.open(`${window.location.origin}/ai-filter/${encoded}`, '_blank');
                        }
                    }
                };
                const offerActionsElements = generateOfferActionsElements(offerLink, offer.link, [similarOfferAction], offer.encryptedId !== null);

                const interactiveElements = generateInteractiveElements(offer);
                interactiveElements.splice(-1);
                const detailsTabContent = saleOfferListFunctions.generateOfferDetailsTabContent(
                    offer,
                    detailsInfoElementsState,
                    hasPredictionRole,
                    setDetailsInfoElementHover,
                    setDetailsInfoElementClicked
                );
                const duplicatesTabContent: V2DuplicatesTabProps = {
                    duplicates: offer.duplicates?.map((duplicate) => {
                        return {
                            id: duplicate.advertisementId,
                            bigImage: true,
                            cardData: generateDuplicateCardData({ ...duplicate, module: offer.module }),
                            onClick: () => {
                            },
                            openOfferTab: () => {
                            }
                        };
                    })
                };
                const flipInfo = normalizeFlipResult(offer.flipResult);

                return {
                    id: offer.advertisementId,
                    onSingleOfferClick: (_: React.MouseEvent,
                        detailsOpen: boolean) => toggleContainerDetails(detailsOpen, offer),
                    baseCardContent: {
                        isHalfTransparent: offer.blockageInfo?.permanentlyBlocked,
                        bigImage: true,
                        cardData: generateCardData(offer),
                        openOfferTab: () => {
                        }
                    },
                    detailsCardData: {
                        interactiveElements: [
                            ...interactiveElements, {
                                icon: 'squares' as IconEnum,
                                tooltipText: isTouchDevice() ? undefined : 'Więcej',
                                onClick: () => isMoreButtonActive ? setMoreButtonActiveId(null) : setMoreButtonActiveId(`${offer.advertisementId}-detail`),
                                additionalJSXSibling: isMoreButtonActive
                                    ? <OfferActions onClickOutside={() => setMoreButtonActiveId(null)} isInModal
                                        elements={offerActionsElements}/>
                                    : undefined
                            }
                        ],
                        modificationDate: offer.lastUpdated,
                        additionalInfoBadge: offer.offerFrom,
                        predictedAdditionalInfoBadge: offer.predictedOfferFrom,
                        thumbnailPath: offer.photos && offer.photos.length > 0 ? convertToThumbnailPath(offer.photos[0], offer.module) : './defaultImg.png',
                        date: offer.dateAdded!,
                        dateIcon: saleOfferListFunctions.getDateIcon(offer.status),
                        showLendiButton: true,
                        onToggleContainerDetails: () => {
                        },
                        tabsContent: {
                            toggleContainerDetails: () => {
                            },
                            loadDetails: () => loadDetails(offer.advertisementId, offer.module),
                            interactiveElements: [
                                ...interactiveElements, {
                                    icon: 'squares' as IconEnum,
                                    tooltipText: isTouchDevice() ? undefined : 'Więcej',
                                    onClick: () => isMoreButtonActive ? setMoreButtonActiveId(null) : setMoreButtonActiveId(`${offer.advertisementId}-detail`),
                                    additionalJSXSibling: isMoreButtonActive
                                        ? <OfferActions onClickOutside={() => setMoreButtonActiveId(null)} isInModal
                                            elements={offerActionsElements}/>
                                        : undefined
                                }
                            ],
                            detailsTabContent: {
                                ...detailsTabContent,
                                hasCalculationRole,
                                hasPredictionRole,
                                withPrediction: true,
                                score: offer.score,
                                scorePrecision: offer.scorePrecision,
                                showLendiButton: true,
                                isDuplicate: offer.originalEncryptedId !== null && offer.originalEncryptedId !== undefined,
                                lowestPriceLabel: offer.duplicateWithLowestPrice,
                                privateOfferLabel: offer.duplicateWithPrivateOffer,
                                archivedLabel: offer.archived,
                                adContext: { adId: offer.advertisementId, adModule: offer.module },
                                ...user.organizationRole ? { organizationContainer: generateOrganizationCardData(offer) } : {}
                            },
                            duplicatesTabContent,
                            modificationTabContent: {
                                modifications: offer.detailedContent?.modifications || []
                            },
                            notesTabContent: {
                                notes: saleOfferState.notes,
                                handleDelete: (noteId: string) => handleDelete(Number(noteId), offer.advertisementId, offer.module),
                                handleSaveNote: (value: string,
                                    id?: string) => handleSaveNote(value, offer.advertisementId, offer.module, Number(id))
                            },
                            calculationsTabContent: offer.module === Module.SALE_FLAT
                                ? {
                                    calculation: true,
                                    hasCalculationRole,
                                    id: offer.advertisementId,
                                    flipInfo,
                                    currentPrice: offer.price,
                                    area: offer.area,
                                    loadFlipCalculations: () => loadFlipCalculations(offer.advertisementId),
                                    calculatorState,
                                    onValueChange: handleCalculatorChange,
                                    onUpdateField: (fieldName: keyof CalculatorSettings,
                                        value: string) => updateFlipCalculations(offer.advertisementId, {
                                        ...calculatorState,
                                        [fieldName]: value
                                    }),
                                    onUpdateTotalField: (fieldName: keyof CalculatorSettings,
                                        value: string) => updateFlipCalculationTotalValue(offer.advertisementId, fieldName, value, offer.price, offer.area, flipInfo?.maxPurchasePrice, flipInfo?.certainPrice),
                                    onResetField: (fieldName: keyof CalculatorSettings) => resetFlipCalculationField(offer.advertisementId, fieldName),
                                    onResetAll: () => resetFlipCalculations(offer.advertisementId)
                                }
                                : undefined,
                            opinionTabContent: {
                                handleSaveOpinion: (rating: number,
                                    content: string) => handleSaveOrUpdateOpinion(offer, rating, content),
                                comments: saleOfferState.scoreAndOpinions?.opinions?.map((opinion) => ({
                                    id: opinion.opinionId,
                                    date: opinion.dateAdded,
                                    content: opinion.content || '',
                                    rating: opinion.numberOfStars,
                                    isUserComment: opinion.loggedUserOpinion
                                })),
                                commentsCount: offer.numberOfCommunityComments,
                                opinionsNumber: offer.numberOfCommunityOpinions,
                                opinionsRating: saleOfferState.scoreAndOpinions?.communityScore || 0,
                                handleDeleteOpinion: (id: number) => deleteOpinion(offer.advertisementId, offer.module, id),
                                applicationScore: offer.score,
                                applicationScorePrecision: offer.scorePrecision
                            },
                            raisesTabContent: offer.detailedContent && offer.detailedContent.raisesValue && offer.detailedContent.raisesValue.length > 0 ? offer.detailedContent.raisesValue as RaisesTabContentProps : undefined,
                            numberOfModifications: offer.numberOfModifications || 0,
                            numberOfDuplicates: offer.numberOfDuplicates || 0,
                            numberOfNotes: offer.numberOfNotes || 0,
                            numberOfRaises: offer.numberOfRaises || 0,
                            handleLoadDuplicates: () => handleDuplicates(offer),
                            handleLoadNotes: () => handleNotes(offer),
                            handleLoadComments: () => loadOpinionsAsync(offer.advertisementId, offer.module)
                        }
                    }
                };
            })
            : [];

        setRenderList(offersList);
    }, [
        saleOfferState.saleOfferList?.content,
        saleOfferState.notes,
        saleOfferState.scoreAndOpinions?.opinions,
        saleOfferState.scoreAndOpinions,
        moreButtonActiveId,
        organizationActiveId,
        calculatorState,
        activeInfoElementId,
        activeDetailInfoElementId,
        infoElementsState
    ]);

    return (
        <div>
            <V2OfferList
                totalPages={saleOfferState.saleOfferList.totalPages}
                handleChangePage={handleChangePage}
                pageNumber={saleOfferState.saleOfferList?.pageable?.pageNumber || 1}
                lastPage={saleOfferState.saleOfferList.last}
                handleOlderAdverts={handleOlderAdverts}
                isLoadMoreButton
                handleActiveFiltersChange={handleActiveFiltersChange}
                showList={!saleOfferState.saleOfferList.empty && saleOfferState.saleOfferList.content?.length > 0}
                activeFiltersState={saleFiltersState}
                filters={getSaleFiltersFromState(saleFiltersState)}
                onDeleteFilter={handleDeleteFilterValue}
                showNoItemsText={saleOfferState.saleOfferList && saleOfferState.saleOfferList.empty && saleOfferState.saleOfferList.content.length === 0 && saleOfferState.saleOfferList.afterSearch}
                renderList={renderList}
            />
        </div>
    );
};

export default SaleOffers;
